import { updateCurrentCompanyActive } from "./api/locations.services";
import { MODULE_PERMISSION_CACHE_KEY } from "../context/ModulesContext";
import { currentLanguage } from "../context/languageSegment";
import { getSupportedLocale } from "./localeLoader";
import { FALLBACK_MODULE_PATH, resolveModulePath } from "../util/defaultModule";
import { menuSidebarTest } from "../component/sidebarMenu";

/** The role's load module as a registered route; only static route tables are consulted, never a server path. */
export const landingPathFor = (loadModule: unknown): string => {
  const group = typeof (loadModule as any)?.group === "string" ? (loadModule as any).group : "";
  const name = typeof (loadModule as any)?.name === "string" ? (loadModule as any).name : "";
  if (!name) return FALLBACK_MODULE_PATH;
  const known = resolveModulePath(group, name);
  if (known) return known;
  // Same lookup as the sign-in redirect.
  for (const menu of menuSidebarTest) {
    if (menu.title === group && menu.children) {
      const child = menu.children.find((c) => c.title === name);
      if (child) return child.path;
    }
    if (menu.title === name && menu.path) return menu.path;
  }
  return FALLBACK_MODULE_PATH;
};

const goTo = (url: string) => window.location.assign(url);

// finex-web editors with unsaved changes cancel this, open their leave dialog and call detail.proceed() on Discard.
export const LEAVE_EVENT = "finex:beforeLeave";

/** False when an open page held the leave; that page may call `proceed` (run at most once) later. */
export const confirmLeave = (proceed: () => void): boolean => {
  let used = false;
  const once = () => {
    if (used) return;
    used = true;
    proceed();
  };
  const event = new CustomEvent(LEAVE_EVENT, { cancelable: true, detail: { proceed: once } });
  document.dispatchEvent(event);
  return !event.defaultPrevented;
};

export interface SwitchOptions {
  /** The caller's whole switch (overlay, error toast), run again when a page that held the leave discards. */
  resume: () => void;
  navigate?: (url: string) => void;
}

// The sidebars stay clickable while a switch runs; a second pick must not start a second session.
let pending: Promise<boolean> | null = null;

/**
 * Switch company, then open the new role's landing page in the URL's language with nothing cached from the old one.
 * Resolves false, with nothing saved, when an open page holds the leave for unsaved changes.
 */
export const switchCompany = (companyId: string, { resume, navigate = goTo }: SwitchOptions): Promise<boolean> => {
  if (pending) return pending;
  if (!confirmLeave(resume)) return Promise.resolve(false);
  pending = runSwitch(companyId, navigate)
    .then(() => true)
    .finally(() => {
      pending = null;
    });
  return pending;
};

const runSwitch = async (companyId: string, navigate: (url: string) => void): Promise<void> => {
  const response: any = await updateCurrentCompanyActive({
    companyId,
    userId: localStorage.getItem("userId") || "",
  });
  const data = response?.data;
  const tokens = data?.tokens;
  if (!tokens?.accessToken || !tokens?.refreshToken || !data?.sessionId || !data?.user?.id || !data?.user?.companyId) {
    throw new Error("The company switch did not return a session");
  }
  const lang = getSupportedLocale(currentLanguage(window.location.pathname));
  localStorage.setItem("accessToken", tokens.accessToken);
  localStorage.setItem("refreshToken", tokens.refreshToken);
  localStorage.setItem("userId", data.user.id);
  localStorage.setItem("companyId", data.user.companyId);
  localStorage.setItem("sessionId", data.sessionId);
  localStorage.setItem("language", lang);
  // The old company's branch and menus must not leak into the new one (the 403 page reads the cache).
  localStorage.removeItem("locationId");
  localStorage.removeItem(MODULE_PERMISSION_CACHE_KEY);
  navigate(`/${lang}${landingPathFor(data.loadModule)}`);
};
